import React, { createContext, useContext, useRef, useState } from "react";
import type { ReportTemplate } from "../../excel/reports/api";

type ReportDataStatus = Record<string, boolean>;

interface ReportDataContextType {
  reportDataStatus: ReportDataStatus;
  refreshReportDataStatus: (templates?: ReportTemplate[]) => Promise<void>;
  refreshReportDataStatusFor: (templates: ReportTemplate[]) => Promise<void>;
  resetReportDataStatus: () => void;
}

const ReportDataContext = createContext<ReportDataContextType | undefined>(undefined);

export const ReportDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reportDataStatus, setReportDataStatus] = useState<ReportDataStatus>({});
  const lastTemplatesRef = useRef<ReportTemplate[]>([]);

  const refreshReportDataStatus = async (templates?: ReportTemplate[]) => {
    const list = templates ?? lastTemplatesRef.current;
    lastTemplatesRef.current = list;

    if (list.length === 0) {
      setReportDataStatus({});
      return;
    }
    await refreshReportDataStatusFor(list);
  };

  const refreshReportDataStatusFor = async (list: ReportTemplate[]) => {
    type Loader = () => Promise<unknown[]>;
    const loaderToSheets = new Map<Loader, string[]>();
    for (const t of list) {
      const fn = t.reportDataSource;
      const sheets = loaderToSheets.get(fn) ?? [];
      sheets.push(t.worksheetName);
      loaderToSheets.set(fn, sheets);
    }

    const fnResultRows = await Promise.all(
      Array.from(loaderToSheets.entries()).map(async ([fn, sheetNames]) => {
        try {
          const has = (await fn()).length > 0;
          return sheetNames.map((name) => [name, has] as [string, boolean]);
        } catch {
          // Loader failed (transient API failure) — omit these sheets from the merge so their
          // previous status is preserved instead of flipping buttons to the no-data state
          return [];
        }
      })
    );
    const dict = Object.fromEntries(fnResultRows.flat());
    setReportDataStatus((prev) => ({ ...prev, ...dict }));
  };

  const resetReportDataStatus = () => {
    setReportDataStatus({});
  };

  return (
    <ReportDataContext.Provider
      value={{ reportDataStatus, resetReportDataStatus, refreshReportDataStatus, refreshReportDataStatusFor }}
    >
      {children}
    </ReportDataContext.Provider>
  );
};

export const useReportData = () => {
  const ctx = useContext(ReportDataContext);
  if (!ctx) throw new Error("useReportData must be used within ReportDataProvider");
  return ctx;
};
